import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import {TranslateService, LangChangeEvent} from '@ngx-translate/core';
import {Subscription} from 'rxjs';

const WIDTHS = [480, 768, 1280, 1920, 2560, 3840] as const;
const DEFAULT_BASE_PATH = 'assets/images/talent-search/redesign';

@Component({
  selector: 'app-responsive-img',
  template: `
    <picture [class]="pictureClass">
      <source
        type="image/webp"
        [attr.srcset]="srcset"
        [attr.sizes]="sizes"
      >
      <img
        [src]="fallbackSrc"
        [alt]="alt"
        [loading]="loading"
        [class]="imgClass"
        [attr.width]="width || null"
        [attr.height]="height || null"
        draggable="false"
      >
    </picture>
  `,
  styles: [':host { display: contents; }'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResponsiveImgComponent implements OnInit, OnDestroy {
  @Input() name!: string;
  @Input() alt = '';
  @Input() loading: 'eager' | 'lazy' = 'lazy';
  @Input() imgClass = '';
  @Input() pictureClass = '';
  @Input() sizes = '100vw';
  @Input() width?: number;
  @Input() height?: number;
  @Input() basePath = DEFAULT_BASE_PATH;
  @Input() langAware = true;

  srcset = '';
  fallbackSrc = '';

  private langSub?: Subscription;
  private lang = 'en';

  constructor(
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.lang = this.normalizeLang(this.translateService.currentLang);
    this.updatePaths();

    this.langSub = this.translateService.onLangChange.subscribe(
      (event: LangChangeEvent) => {
        this.lang = this.normalizeLang(event.lang);
        this.updatePaths();
        this.cdr.markForCheck();
      },
    );
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  private normalizeLang(lang: string): string {
    return lang?.startsWith('ar') ? 'ar' : 'en';
  }

  private updatePaths(): void {
    const base = this.langAware
      ? `${this.basePath}/${this.lang}/${this.name}`
      : `${this.basePath}/${this.name}`;
    this.srcset = WIDTHS.map(w => `${base}-${w}.webp ${w}w`).join(', ');
    this.fallbackSrc = `${base}-768.webp`;
  }
}
